import DT from 'duration-time-conversion';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { ContextMenu, ContextMenuTrigger, MenuItem } from 'react-contextmenu';
import styled from 'styled-components';
import { getKeyCode } from '../../utils';

const Timeline = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    min-height: 100px;
    overflow: hidden;
    background-color: transparent;
    padding-bottom: 0.02em; /* Ensure space for footer */
    pointer-events: none; /* Disable pointer events on the container */
    
    .time-line {
        position: relative;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        pointer-events: none;
    }

    .sub-item {
        position: absolute;
        top: 25%;
        left: 0;
        height: 60%;
        overflow: hidden;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        color: #fff;
        font-size: 14px;
        cursor: move;
        user-select: none;
        pointer-events: auto !important; /* Ensure pointer events are enabled */
        touch-action: none; /* Improve touch handling */
        /* Default color for non-active subtitles */
        background-color: rgba(150, 150, 150, 0.6);
        border: 1px solid rgba(180, 180, 180, 0.7);
        border-radius: 3px;
        z-index: 32;
        transition: background-color 0.2s ease;
        
        /* Style for current subtitle */
        &.sub-highlight {
            background-color: rgba(70, 130, 180, 0.7);
            border: 1px solid rgba(100, 150, 200, 0.8);
            box-shadow: 0 0 8px rgba(70, 130, 180, 0.5);
        }
        
        /* Style for illegal subtitles */
        &.sub-illegal {
            background-color: rgba(220, 50, 50, 0.6);
            border: 1px solid rgba(240, 80, 80, 0.7);
        }
    }
    
    /* Style for handles to make them more visible */
    .sub-handle {
        background-color: rgba(255, 255, 255, 0.3);
        position: absolute;
        top: 0;
        bottom: 0;
        height: 100%;
        width: 10px; /* Ensure handle width is sufficient */
        cursor: col-resize;
        pointer-events: auto !important;
        z-index: 34; /* Ensure handles are above subtitle content */
        touch-action: none; /* Improve touch handling */
    }
    
    /* Style for subtitle text to ensure readability */
    .sub-text {
        flex: 1;
        padding: 4px;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
    }
    
    /* Make duration more visible */
    .sub-duration {
        position: absolute;
        bottom: 2px;
        width: 100%;
        text-align: center;
        font-size: 10px;
        opacity: 0.8;
    }

    .sub-item:active {
        outline: 2px solid red !important;
    }
    .sub-handle:active {
        background-color: yellow !important;
    }

    /* Style for dragging effect */
    .dragging {
        opacity: 0.8;
        border-color: rgba(255, 255, 255, 0.8) !important;
        box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
    }
`;

function getCurrentSubs(subs, beginTime, duration) {
    return subs.filter((item) => {
        return (
            (item.startTime >= beginTime && item.startTime <= beginTime + duration) ||
            (item.endTime >= beginTime && item.endTime <= beginTime + duration) ||
            (item.startTime < beginTime && item.endTime > beginTime + duration)
        );
    });
}

function magnetically(time, closeTime) {
    if (!closeTime) return time;
    if (time > closeTime - 0.1 && closeTime + 0.1 > time) {
        return closeTime;
    }
    return time;
}

export default React.memo(
    function ({ player, subtitle, render, currentTime, checkSub, removeSub, hasSub, updateSub, mergeSub, viewEng }) {
        // Add this ref to track the last selected subtitle
        const lastSubRef = useRef(null);
        const $blockRef = React.createRef();
        const $subsRef = React.createRef();
        // Add this calibration constant at the top of your component
        const TIME_CALIBRATION = 0.065; // Adjust this value to correct for the offset you're seeing

        // Convert global variables to state or refs for better React compatibility
        const [isDragging, setIsDragging] = useState(false);
        const dragInfoRef = useRef({
            sub: null,
            type: '',
            x: 0,
            index: -1, 
            target: null,
            width: 0,
            startLeft: 0,
            diffX: 0
        });
        
        // Find the current subtitle based on playback time
        const getCurrentSubtitle = useCallback(() => {
            if (!subtitle || !subtitle.length) return -1;
            
            for (let i = 0; i < subtitle.length; i++) {
                const sub = subtitle[i];
                if (currentTime >= sub.startTime && currentTime <= sub.endTime) {
                    return i;
                }
            }
            return -1;
        }, [subtitle, currentTime]);
        
        // Get the current subtitle index based on playback time
        const currentIndex = getCurrentSubtitle();

        // Add this line to define currentSubs
        const currentSubs = React.useMemo(() => 
            getCurrentSubs(subtitle, render.beginTime, render.duration), 
            [subtitle, render.beginTime, render.duration]
        );
        
        // With this more reliable calculation:
        const gridGap = React.useMemo(() => {
            if ($blockRef.current) {
                return $blockRef.current.clientWidth / render.gridNum;
            }
            return document.body.clientWidth / render.gridNum;
        }, [$blockRef, render.gridNum]);

        // Rewrite onMouseDown to use component state/refs and add debugging
        const onMouseDown = useCallback((sub, event, type = '') => {
            
            // Add debugging
            console.log(`Timeline onMouseDown called:`, {
                sub,
                eventType: event.type,
                mouseButton: event.button,
                eventTarget: event.target,
                type
            });
            
            // Only handle left mouse button
            if (event.button !== 0) return;
            
            // Find the subtitle element directly from the event path
            // This is more reliable than using indices
            let targetElement = event.target.closest('.sub-item');
            if (!targetElement) {
                console.warn("Could not find parent .sub-item element");
                return;
            }
            
            // Update state
            setIsDragging(true);
            
            // Store all drag-related info in the ref
            dragInfoRef.current = {
                sub,
                type,
                x: event.clientX, // Use clientX instead of pageX for more reliable coordinates
                index: currentSubs.indexOf(sub),
                target: targetElement,
                width: parseFloat(targetElement.style.width || '0'),
                startLeft: parseFloat(targetElement.style.left || '0'),
                diffX: 0
            };
            
            console.log("Drag started with info:", {
                subIndex: dragInfoRef.current.index,
                target: targetElement,
                width: dragInfoRef.current.width,
                startLeft: dragInfoRef.current.startLeft
            });
            
            // Prevent event from propagating to parent elements
            event.stopPropagation();
            
            // Prevent default browser actions like text selection
            event.preventDefault();

            // Add dragging class for visual feedback
            targetElement.classList.add('dragging');
        }, [currentSubs]);

        // Update your mousemove handler to use the ref
        const onDocumentMouseMove = useCallback((event) => {
            if (!isDragging || !dragInfoRef.current.target) return;
            
            const { target, type, x, width, startLeft } = dragInfoRef.current;
            const diffX = event.clientX - x; // Use clientX for consistency
            dragInfoRef.current.diffX = diffX;
            
            // Update element position/size based on drag type
            if (type === 'left') {
                // Resize from left side (changing start time)
                const newWidth = Math.max(20, width - diffX); // Ensure minimum width
                target.style.width = `${newWidth}px`;
                target.style.left = `${startLeft + (width - newWidth)}px`;
            } else if (type === 'right') {
                // Resize from right side (changing end time)
                target.style.width = `${Math.max(20, width + diffX)}px`; // Ensure minimum width
            } else {
                // Move the entire subtitle
                target.style.left = `${startLeft + diffX}px`;
            }
        }, [isDragging]);

        // Find the current subtitle based on playback time
        const getSubtitleIndex = useCallback((subtime) => {
            if (!subtitle || !subtitle.length) return -1;
            
            for (let i = 0; i < subtitle.length; i++) {
                const sub = subtitle[i];
                if (subtime === sub.startTime) {
                    return i;
                }
            }
            return -1;
        }, [subtitle]);
        
        // Update mouseup handler to properly apply changes
        const onDocumentMouseUp = useCallback(() => {
            if (!isDragging || !dragInfoRef.current.target) return;
            
            const { target, sub, type, diffX, startLeft, width } = dragInfoRef.current;
            
            // Remove dragging class
            target.classList.remove('dragging');
            
            try {
                // Calculate the time changes based on the grid
                const pxToTime = (px) => px / (gridGap * 10);
                
                // Get the current element position and size
                const currentLeft = parseFloat(target.style.left);
                const currentWidth = parseFloat(target.style.width);
                
                // Calculate new times
                let newStartTime, newEndTime;
                
                if (type === 'left') {
                    // Changed start time by dragging left handle
                    newStartTime = render.beginTime + pxToTime(currentLeft - render.padding * gridGap) + TIME_CALIBRATION;
                    newEndTime = sub.endTime;
                } else if (type === 'right') {
                    // Changed end time by dragging right handle
                    newStartTime = sub.startTime;
                    newEndTime = newStartTime + pxToTime(currentWidth);
                } else {
                    // Moved entire subtitle
                    const timeDiff = pxToTime(diffX);
                    newStartTime = sub.startTime + timeDiff;
                    newEndTime = sub.endTime + timeDiff;
                }
                
                console.log("New calculated times:", {
                    newStartTime,
                    newEndTime,
                    diffTime: newEndTime - newStartTime
                });
                
                // Validate the new times
                if (newStartTime >= 0 && newEndTime > newStartTime && (newEndTime - newStartTime) >= 0.2) {
                    // Format the times properly
                    const newStart = DT.d2t(newStartTime);
                    const newEnd = DT.d2t(newEndTime);
                    
                    
                    
                    const subIndex = getSubtitleIndex(sub.startTime);
                    
                    const previous = subtitle[subIndex - 1];
                    const next = subtitle[subIndex + 1];
                    
                    let hasCollision = false;
                    
                    // Check collision with previous subtitle
                    if (previous && newStartTime < previous.endTime) {
                        hasCollision = true;
                        console.warn(`Collision with previous subtitle, start: ${previous.startTime}, end: ${previous.endTime}`);
                    }
                    
                    // Check collision with next subtitle
                    if (next && newEndTime > next.startTime) {
                        hasCollision = true;
                        console.warn(`Collision with next subtitle, start: ${next.startTime}, end: ${next.endTime}`);
                    }
                    
                    if (!hasCollision) {
                        // Apply the change - ensure we're setting ALL required fields
                        console.log("Updating subtitle with new times:", { 
                            start: newStart, 
                            end: newEnd 
                        });
                        
                        // Make sure to update both regular times and Estart/Eend times
                        updateSub(sub, {
                            start: newStart,
                            end: newEnd,
                            Estart: newStart,  // Make sure English times are updated too
                            Eend: newEnd,
                            startTime: newStartTime,
                            endTime: newEndTime
                        });
                    } else {
                        // Reset to original position on collision
                        console.log("Collision detected, resetting position");
                        resetSubtitlePosition(target, sub, render.beginTime, gridGap, render.padding);
                    }
                } else {
                    // Invalid time range, reset to original position
                    console.log("Invalid time range, resetting position");
                    resetSubtitlePosition(target, sub, render.beginTime, gridGap, render.padding);
                }
            } catch (error) {
                console.error("Error in mouseup handler:", error);
                // Reset in case of error
                resetSubtitlePosition(target, sub, render.beginTime, gridGap, render.padding);
            }
            
            // Reset drag state
            setIsDragging(false);
            dragInfoRef.current = {
                sub: null,
                type: '',
                x: 0,
                index: -1,
                target: null,
                width: 0,
                startLeft: 0,
                diffX: 0
            };
        }, [gridGap, hasSub, subtitle, updateSub, isDragging, render.beginTime, render.padding]);

        // Add this helper function to reset subtitle position
        const resetSubtitlePosition = useCallback((target, sub, beginTime, gridGap, padding) => {
            if (!target) return;
            
            const leftPos = padding * gridGap + (sub.startTime - TIME_CALIBRATION - beginTime) * gridGap * 10;
            const width = Math.max(20, (sub.endTime - sub.startTime) * gridGap * 10);
            
            target.style.left = `${leftPos}px`;
            target.style.width = `${width}px`;
        }, []);

        const onKeyDown = useCallback(
            (event) => {
                const sub = currentSubs[dragInfoRef.current.index];
                if (sub && dragInfoRef.current.target) {
                    const keyCode = getKeyCode(event);
                    switch (keyCode) {
                        case 37:
                            updateSub(sub, {
                                start: DT.d2t(sub.startTime - 0.1),
                                end: DT.d2t(sub.endTime - 0.1),
                                Estart: DT.d2t(sub.startTime - 0.1),
                                Eend: DT.d2t(sub.endTime - 0.1),
                            });
                            console.log(`Seeking to time - onKeyDown - 1: ${sub.startTime - 0.1}`);
                            player.updateTime(sub.startTime - 0.1 + TIME_CALIBRATION);
                            break;
                        case 39:
                            updateSub(sub, {
                                start: DT.d2t(sub.startTime + 0.1),
                                end: DT.d2t(sub.endTime + 0.1),
                                Estart: DT.d2t(sub.startTime + 0.1),
                                Eend: DT.d2t(sub.endTime + 0.1),
                            });
                            console.log(`Seeking to time - onKeyDown - 2: ${sub.startTime + 0.1}`);
                            player.updateTime(sub.startTime + 0.1 + TIME_CALIBRATION);
                            break;
                        case 8:
                        case 46:
                            removeSub(sub);
                            break;
                        default:
                            break;
                    }
                }
            },
            [currentSubs, player, removeSub, updateSub],
        );

        useEffect(() => {
            // Use passive: false for event listeners that call preventDefault
            // This improves performance and avoids warnings
            const moveHandler = (e) => onDocumentMouseMove(e);
            const upHandler = (e) => onDocumentMouseUp(e);
            
            // Add the event listeners
            document.addEventListener('mousemove', moveHandler, { passive: false });
            document.addEventListener('mouseup', upHandler, { passive: false });
            window.addEventListener('keydown', onKeyDown);
            
            // Cleanup function
            return () => {
                document.removeEventListener('mousemove', moveHandler);
                document.removeEventListener('mouseup', upHandler);
                window.removeEventListener('keydown', onKeyDown);
            };
        }, [onDocumentMouseMove, onDocumentMouseUp, onKeyDown]);

        useEffect(() => {
            const handleResize = () => {
                if ($blockRef.current && $subsRef.current) {
                    const footerHeight = $blockRef.current.closest('.footer')?.clientHeight || 220;
                    
                    // Adjust subtitle box height based on footer size
                    const subItemElements = $blockRef.current.querySelectorAll('.sub-item');
                    const optimalHeight = Math.min(60, Math.max(40, footerHeight * 0.3)); // 30% of footer height
                    
                    subItemElements.forEach(item => {
                        item.style.height = `${optimalHeight}px`;
                        item.style.top = `${(100 - optimalHeight) / 2}%`;
                    });
                }
            };
            
            // Initial sizing
            handleResize();
            
            // Add listener for resize events
            window.addEventListener('resize', handleResize);
            
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }, []);

        // Add this to your component to debug updateSub
        useEffect(() => {
            // Create a wrapper function to log what's happening in updateSub
            const debugUpdateSub = (sub, updates) => {
                console.log("updateSub called with:", {
                    subtitle: sub,
                    updates: updates
                });
                
                // Call the actual updateSub
                updateSub(sub, updates);
            };
            
            // Store the original updateSub
            const originalUpdateSub = updateSub;
            
            // Replace updateSub with our debug version (just for logging)
            window._debugUpdateSub = debugUpdateSub;
            
            return () => {
                // Clean up
                delete window._debugUpdateSub;
            };
        }, [updateSub]);

        // Add this function to directly add click handlers for seeking
        const handleSubtitleClick = useCallback((sub, event) => {
            // Only process if it's a genuine click (not the start of a drag)
            if (!isDragging && player?.element) {
                // Calculate the click position within the subtitle element
                const rect = event.currentTarget.getBoundingClientRect();
                const clickPositionRatio = (event.clientX - rect.left) / rect.width;
                
                // Calculate the time corresponding to the click position
                const subtitleDuration = sub.endTime - sub.startTime;
                const clickTime = sub.startTime + (subtitleDuration * clickPositionRatio) + TIME_CALIBRATION;
                
                console.log(`Seeking to clicked position: ${clickTime} (ratio: ${clickPositionRatio.toFixed(2)})`);
                player.updateTime(clickTime);
                
                // Prevent event from propagating
                event.stopPropagation();
            }
        }, [player, isDragging]);

        // Add a function to update lastSubRef when context menu is opened
        const handleContextMenu = useCallback((sub) => {
            console.log("Context menu opened for subtitle:", sub);
            lastSubRef.current = sub;
        }, []);

        // Debug mousedown events in capture phase
        const captureHandler = (e) => {
            console.log("Mousedown event captured:", {
                target: e.target,
                className: e.target.className,
                phase: "capture"
            });
        };
        
        // Debug mousedown events in bubbling phase
        const bubbleHandler = (e) => {
            console.log("Mousedown event bubbled:", {
                target: e.target,
                className: e.target.className,
                phase: "bubble"
            });
        };
        
        // Update the subtitle divs with onClick handler and better event handling
        return (
            <Timeline ref={$blockRef}>
                <div 
                    className="time-line" 
                    ref={$subsRef}
                >
                    {subtitle.length > 0 ? (
                        subtitle.map((sub, key) => {
                            
                            // Then modify the position calculation (around line 550)
                            const leftPos = Math.round(render.padding * gridGap + (sub.startTime - TIME_CALIBRATION - render.beginTime) * gridGap * 10);
                            const width = Math.max(20, Math.round((sub.endTime - sub.startTime) * gridGap * 10));
                            
                            // Determine if this is the current subtitle
                            const isCurrentSub = key === currentIndex;
                            const isIllegalSub = checkSub(sub);
                            
                            return (
                                <div
                                    className={[
                                        'sub-item',
                                        isCurrentSub ? 'sub-highlight' : '',
                                        isIllegalSub ? 'sub-illegal' : '',
                                    ]
                                        .join(' ')
                                        .trim()}
                                    key={key}
                                    style={{
                                        left: `${leftPos}px`,
                                        width: `${width}px`,
                                        position: 'absolute',
                                        zIndex: isCurrentSub ? 33 : 32, // Higher z-index for current subtitle
                                    }}
                                    onClick={(e) => handleSubtitleClick(sub, e)}
                                    onDoubleClick={() => {
                                        if (player.element.duration >= sub.startTime) {
                                            console.log(`Seeking to time - onDoubleClick: ${sub.startTime + 0.001}`);
                                            player.updateTime(sub.startTime + 0.001 + + TIME_CALIBRATION);
                                        }
                                    }}
                                    onContextMenu={() => handleContextMenu(sub)} // Add this
                                >
                                    <ContextMenuTrigger 
                                        id="contextmenu" 
                                        holdToDisplay={-1}
                                        collect={() => ({ subtitle: sub })}
                                    >
                                        <div
                                            className="sub-handle"
                                            style={{
                                                left: 0,
                                                width: 10,
                                            }}
                                            onMouseDown={(event) => {
                                                console.log("Left handle mousedown");
                                                onMouseDown(sub, event, 'left');
                                            }}
                                        ></div>

                                        <div
                                            className="sub-text"
                                            title={viewEng || !sub.text2 ? sub.text : sub.text2}
                                            onMouseDown={(event) => {
                                                console.log("Sub text mousedown");
                                                onMouseDown(sub, event);
                                            }}
                                        >
                                            {`${viewEng || !sub.text2 ? sub.text : sub.text2}`
                                                .split(/\r?\n/)
                                                .map((line, index) => (
                                                    <div key={index} style={{ padding: '0', margin: '0', lineHeight: '1' }}>
                                                        {line}
                                                    </div>
                                                ))}
                                        </div>

                                        <div
                                            className="sub-handle"
                                            style={{
                                                right: 0,
                                                width: 10,
                                            }}
                                            onMouseDown={(event) => {
                                                console.log("Right handle mousedown");
                                                onMouseDown(sub, event, 'right');
                                            }}
                                        ></div>
                                        <div className="sub-duration">{sub.duration} s</div>
                                    </ContextMenuTrigger>
                                </div>
                            );
                        })
                    ) : (
                        <div style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
                            No subtitles in current view range
                        </div>
                    )}
                </div>
                <ContextMenu id="contextmenu">
                    <MenuItem onClick={(e, data) => {
                        // Use data.subtitle if available, otherwise fall back to lastSubRef
                        const subtitleToRemove = data.subtitle || lastSubRef.current;
                        if (subtitleToRemove) {
                            removeSub(subtitleToRemove);
                        }
                    }}>
                        <div value="Delete" />
                    </MenuItem>
                    <MenuItem onClick={(e, data) => {
                        // Use data.subtitle if available, otherwise fall back to lastSubRef
                        const subtitleToMerge = data.subtitle || lastSubRef.current;
                        if (subtitleToMerge) {
                            mergeSub(subtitleToMerge);
                        }
                    }}>
                        <div value="Merge" />
                    </MenuItem>
                </ContextMenu>
            </Timeline>
        );
    }
);
